import React, {
  forwardRef,
  Ref,
  useId,
  useState,
  useImperativeHandle,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';

// Types
import { InputPKProps, ImperativeRefInputPK } from './types';

// Components Styled
import { Container, InputValue, Label } from './styles';

// Media
import { useIsLandscapeTablet } from '../../../hooks/useMediaBreakPoints';
import isEqual from 'lodash.isequal';

const PK_REGEX = /^\d+\s?\+\s?\d{1,4}$/;

/**
 * Primary UI component for PK (punto kilométrico) filtering in tables.
 * Accepts format "km+meters" e.g. "82+800".
 *
 * NOTE: This component is for **table filters** only. Forms that need a PK
 * input use the canonical pattern `<InputText {...register('pkInit')} />` +
 * yup pattern validation (see `LocationForm/index.tsx`).
 */
function InputPK(
  {
    label = 'PK',
    disabled,
    onChangeInputPK,
    ...props
  }: InputPKProps,
  innerRef: Ref<ImperativeRefInputPK>
): JSX.Element {
  const { t } = useTranslation(['common']);
  const [inputValue, setInputValue] = useState<string>('');

  const onChangeInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const val = event.target.value;
      setInputValue(val);
      if (val === '') {
        onChangeInputPK(undefined, undefined);
        return;
      }
      if (PK_REGEX.test(val)) {
        const parts = val.replace(/ /g, '').split('+');
        onChangeInputPK(Number(parts[0]), Number(parts[1]));
      }
      // Si formato inválido, no se llama callback — esperamos input completo
    },
    [onChangeInputPK]
  );

  useImperativeHandle(innerRef, () => ({
    clearSelected: () => {
      setInputValue('');
      // Do NOT call onChangeInputPK here: the debounced PK handlers carry stale
      // closure state (e.g. classification) from before the clear, which would
      // overwrite the correct cursor values dispatched by onClearValue directly.
      // onClearValue already dispatches pkInitKm/pkEndKm/pkInitMeter/pkEndMeter: undefined itself.
    },
  }));

  const id = useId();
  const isLandscapeTablet = useIsLandscapeTablet();

  return (
    <Container {...props}>
      {label && (
        <Label
          component={'label'}
          htmlFor={`${id}-pk-input`}
          colorText="black"
          size={isLandscapeTablet ? 'XS' : 'S'}
        >
          {label}
        </Label>
      )}
      <InputValue
        id={`${id}-pk-input`}
        type="text"
        disabled={disabled}
        placeholder={t('common:pkExample')}
        value={inputValue}
        onChange={onChangeInput}
      />
    </Container>
  );
}

export default React.memo(forwardRef(InputPK), isEqual);
